import React from "react";
import ReactHtmlParser from "react-html-parser";

/**
 * Reusable Modal Component
 *
 * A flexible modal dialog component that supports both plain text and HTML content.
 * Can be configured with custom buttons, titles, and callback functions.
 *
 * @param {Object} props - Component props
 * @param {boolean} props.open - Controls modal visibility
 * @param {string} props.title - Modal header title
 * @param {string} props.content - Plain text content for modal body
 * @param {string} props.savetitle - Text for the primary action button
 * @param {Function} props.savefunction - Callback for primary action button
 * @param {string} props.canceltitle - Text for the cancel button
 * @param {string} props.buttonclass - CSS class for the primary button styling
 * @param {Function} props.updateOpen - Callback to update modal open state
 * @param {Function|null} props.cancelfunction - Optional custom cancel callback
 * @param {string|null} props.datacontent - HTML content for modal body (overrides content)
 * @param {boolean} props.hidecancel - Whether to hide the cancel button
 */
const Modal = ({
    open,
    title,
    content,
    savetitle,
    savefunction,
    canceltitle,
    buttonclass,
    updateOpen,
    cancelfunction = null,
    datacontent = null,
    hidecancel = false,
}) => (
    <div className={`modal-backdrop ${open ? "open" : ""}`}>
        {/* Modal dialog container */}
        <div className="modal-dialog" role="document">
            <div className="modal-content">

                {/* Modal header with title and close button */}
                <div className="modal-header">
                    <h5 className="modal-title">{title}</h5>

                    {/* Close button (X) */}
                    <button
                        type="button"
                        className="close"
                        onClick={() => updateOpen(false)}
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                {/* Modal body content */}
                <div className="modal-body">
                    {datacontent ? (
                        // Support both HTML strings and direct JSX content
                        typeof datacontent === "string"
                            ? ReactHtmlParser(datacontent)
                            : datacontent
                    ) : (
                        // Otherwise render plain text content
                        <p>{content}</p>
                    )}
                </div>

                {/* Modal footer with action buttons */}
                <div className="modal-footer">
                    {/* Cancel button (conditionally rendered) */}
                    {!hidecancel && canceltitle && (
                        <button
                            type="button"
                            className="btn"
                            onClick={() =>
                                cancelfunction
                                    ? cancelfunction() // Use custom cancel function if provided
                                    : updateOpen(false) // Otherwise just close modal
                            }
                        >
                            {canceltitle}
                        </button>
                    )}

                    {/* Primary action button */}
                    <button
                        type="button"
                        className={`btn ${buttonclass}`}
                        onClick={savefunction}
                    >
                        {savetitle}
                    </button>
                </div>
            </div>
        </div>
    </div>
);

export default Modal;
